import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Clear all auth data
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('id');
      localStorage.removeItem('username');
      
      // Redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  checkAdmin: () => api.get('auth/check-admin'),
  registerAdmin: (data) => api.post('auth/register-admin', data),
  login: (data) => api.post('auth/login', data),
};

// Users API
export const usersAPI = {
  createUser: (data) => api.post('users', data),
  getAllUsers: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`users${queryString ? `?${queryString}` : ''}`);
  },
  getPublicUsers: () => api.get('users/public'),
  getCurrentUser: () => api.get('users/user-public'),
  updateUser: (id, data) => api.put(`users/${id}`, data),
  deleteUser: (id) => api.delete(`users/${id}`),
  getUserAssignedLeads: (userId) => api.get(`users/${userId}/assigned-leads`),
};

// Leads API
export const leadsAPI = {
  createLead: (data) => api.post('/leads', data),
  getAllLeads: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/leads${queryString ? `?${queryString}` : ''}`);
  },
  getLead: (id) => api.get(`/leads/${id}`),
  updateLead: (id, data) => api.put(`/leads/${id}`, data),
  deleteLead: (id) => api.delete(`/leads/${id}`),
  getAssignedLeads: (userId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/leads/assigned/${userId}${queryString ? `?${queryString}` : ''}`);
  },
  getUserStats: (userId) => api.get(`/leads/user-stats/${userId}`),
  addLeadHistory: (id, data) => api.post(`/leads/${id}/history`, data),
  updateLeadHistory: (leadId, historyId, data) => api.put(`/leads/${leadId}/history/${historyId}`, data),
  updateExpectedValue: (id, expectedValue) => api.put(`/leads/${id}/expected-value`, { expectedValue }),
  addRating: (id, data) => api.post(`/leads/${id}/rating`, data),
  assignLead: (id, data) => api.post(`/leads/${id}/assign`, data),
  unassignLead: (id) => api.delete(`/leads/${id}/assign`),
  getLeadStats: () => api.get('/leads/stats/overview'),
  getStatusCards: () => api.get('/leads/status-cards'),
  getDemoLeads: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/leads/demos${queryString ? `?${queryString}` : ''}`);
  },
  updateDemoInfo: (id, data) => api.put(`/leads/${id}/demo`, data),
  updateAssignees: (id, data) => api.put(`/leads/${id}/assignees`, data),
};

// Notifications API
export const notificationsAPI = {
  getNotifications: () => api.get('notifications'),
  getUnreadCount: () => api.get('notifications/unread/count'),
  markAsRead: (id) => api.put(`notifications/${id}/read`),
  markAllAsRead: () => api.put('notifications/read/all'),
  deleteNotification: (id) => api.delete(`notifications/${id}`),
  deleteAllRead: () => api.delete('notifications/read/all'),
};

// Email Campaigns API
export const emailCampaignsAPI = {
  addEmailCampaign: (leadId, data) => api.post(`email-campaigns/${leadId}`, data),
  updateEmailCampaign: (leadId, campaignId, data) => api.put(`email-campaigns/${leadId}/${campaignId}`, data),
  deleteEmailCampaign: (leadId, campaignId) => api.delete(`email-campaigns/${leadId}/${campaignId}`),
};

// Email Generator API
export const emailGeneratorAPI = {
  generateEmail: (data) => api.post('/email-generator/generate', data),
};

// Demo API
export const demoAPI = {
  getDemoLeads: () => api.get('/leads/demos'),
  updateDemoInfo: (id, data) => api.put(`/leads/${id}/demo`, data),
};

// Edit Requests API
export const editRequestsAPI = {
  createEditRequest: (data) => api.post('/edit-requests', data),
  getAllEditRequests: () => api.get('/edit-requests'),
  getPendingCount: () => api.get('/edit-requests/pending/count'),
  updateEditRequest: (id, data) => api.put(`/edit-requests/${id}`, data),
};

// Evaluation Pilot API
export const evaluationPilotAPI = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/evaluation-pilot${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id) => api.get(`/evaluation-pilot/${id}`),
  getByLeadId: (leadId) => api.get(`/evaluation-pilot/lead/${leadId}`),
  update: (id, data) => api.put(`/evaluation-pilot/${id}`, data),
};

// Closed Won API
export const closedWonAPI = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/closed-won${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id) => api.get(`/closed-won/${id}`),
  getByLeadId: (leadId) => api.get(`/closed-won/lead/${leadId}`),
  update: (id, data) => api.put(`/closed-won/${id}`, data),
};

// Lead Generation API
export const leadGenerationAPI = {
  scrapeSchools: (city) => api.post('/lead-generation/scrape-schools', { city }),
  createBulkLeads: (schools) => api.post('/lead-generation/create-bulk-leads', { schools }),
};

// Onboarding API
export const onboardingAPI = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/onboarding${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id) => api.get(`/onboarding/${id}`),
  getByLeadId: (leadId) => api.get(`/onboarding/lead/${leadId}`),
  create: (data) => api.post('/onboarding', data),
  update: (id, data) => api.put(`/onboarding/${id}`, data),
  
  // Teacher operations
  uploadTeachers: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/onboarding/${id}/teachers/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  saveTeachers: (id, teachers) => api.post(`/onboarding/${id}/teachers/save`, { teachers }),
  getTeachers: (id) => api.get(`/onboarding/${id}/teachers`),
  updateTeacher: (onboardingId, teacherId, data) => api.put(`/onboarding/${onboardingId}/teachers/${teacherId}`, data),
  deleteTeacher: (onboardingId, teacherId) => api.delete(`/onboarding/${onboardingId}/teachers/${teacherId}`),
  
  // Student operations
  uploadStudents: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/onboarding/${id}/students/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  saveStudents: (id, students) => api.post(`/onboarding/${id}/students/save`, { students }),
  getStudents: (id) => api.get(`/onboarding/${id}/students`),
  updateStudent: (onboardingId, studentId, data) => api.put(`/onboarding/${onboardingId}/students/${studentId}`, data),
  deleteStudent: (onboardingId, studentId) => api.delete(`/onboarding/${onboardingId}/students/${studentId}`),
};

export default api;